import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SEO from '../../components/common/SEO';
import QRWidget from '../../components/common/QRWidget';

/* ─── Design tokens ─────────────────────────────────────── */
const C = {
  navy: '#0a1628', blue: '#1d4ed8', blueHov: '#1e40af',
  blueLt: '#eff6ff', blueMd: '#dbeafe',
  teal: '#0891b2', green: '#059669',
  text: '#0f172a', sub: '#475569', muted: '#94a3b8',
  border: '#e2e8f0', bg: '#f8fafc', white: '#ffffff',
};

/* ─── Scroll-reveal ──────────────────────────────────────── */
function FadeIn({ children, delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.06 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const tx = direction === 'up' ? '0,22px,0' : direction === 'left' ? '22px,0,0' : '-22px,0,0';
  return (
    <div ref={ref} style={{
      transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
      opacity: vis ? 1 : 0, transform: vis ? 'translate3d(0,0,0)' : `translate3d(${tx})`,
    }}>{children}</div>
  );
}

/* ─── Product image ──────────────────────────────────────── */
function ProductImage({ item, height = 240, large = false }) {
  const [failed, setFailed] = useState(false);
  const isImp = item?.category === 'impurity';
  const accent = isImp ? C.blue : C.green;
  const bg1 = isImp ? '#eff6ff' : '#f0fdf4';
  const bg2 = isImp ? '#dbeafe' : '#dcfce7';

  if (item?.image && !failed) {
    return (
      <div style={{ width: '100%', height, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: large ? 16 : '12px 12px 0 0', overflow: 'hidden' }}>
        <img src={`/products/${item.image}`} alt={item.name}
          style={{ maxHeight: height - 20, maxWidth: '88%', objectFit: 'contain' }}
          onError={() => setFailed(true)} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height, background: `linear-gradient(135deg,${bg1},${bg2})`, borderRadius: large ? 16 : '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
      <svg width="160" height="120" viewBox="0 0 160 120" style={{ position: 'absolute', opacity: 0.12 }}>
        <circle cx="32" cy="60" r="16" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="80" cy="24" r="16" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="128" cy="60" r="16" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="80" cy="96" r="16" stroke={accent} strokeWidth="2" fill="none" />
        <line x1="48" y1="54" x2="64" y2="36" stroke={accent} strokeWidth="2" />
        <line x1="96" y1="36" x2="112" y2="54" stroke={accent} strokeWidth="2" />
        <line x1="112" y1="66" x2="96" y2="84" stroke={accent} strokeWidth="2" />
        <line x1="64" y1="84" x2="48" y2="66" stroke={accent} strokeWidth="2" />
      </svg>
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ width: large ? 64 : 48, height: large ? 64 : 48, borderRadius: large ? 18 : 14, background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: large ? '1rem' : '0.82rem', margin: '0 auto 8px', boxShadow: `0 6px 20px ${accent}44` }}>
          {isImp ? 'IMP' : 'RS'}
        </div>
        <div style={{ fontSize: '0.7rem', color: accent, fontWeight: 700 }}>{isImp ? 'Impurity Standard' : 'Reference Standard'}</div>
      </div>
    </div>
  );
}

/* ─── Spec row ───────────────────────────────────────────── */
function SpecRow({ label, value, mono, highlight }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '11px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0, minWidth: 140 }}>{label}</span>
      <span style={{ fontSize: '0.875rem', fontWeight: highlight ? 700 : 500, color: highlight ? C.blue : C.text, fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

/* ─── Related product card ───────────────────────────────── */
function RelatedCard({ item }) {
  const [hov, setHov] = useState(false);
  const navigate = useNavigate();
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={() => navigate(`/catalog/${item.id}`)}
      style={{
        background: C.white, borderRadius: 14, border: `1px solid ${hov ? C.blue + '40' : C.border}`,
        overflow: 'hidden', cursor: 'pointer',
        boxShadow: hov ? '0 10px 28px rgba(29,78,216,0.12)' : '0 2px 8px rgba(0,0,0,0.05)',
        transform: hov ? 'translateY(-3px)' : 'none',
        transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column',
      }}>
      <ProductImage item={item} height={110} />
      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: C.text, lineHeight: 1.4 }}>{item.name}</div>
        <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: 'monospace' }}>{item.catalog_number}</div>
        {item.cas_number && <div style={{ fontSize: '0.72rem', color: C.sub }}>CAS: {item.cas_number}</div>}
        {item.therapeutic_class && (
          <div style={{ fontSize: '0.68rem', background: C.blueLt, color: C.blue, padding: '2px 8px', borderRadius: 20, alignSelf: 'flex-start', fontWeight: 700 }}>
            {item.therapeutic_class}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: item.stock_status === 'in_stock' ? '#d1fae5' : '#fef3c7', color: item.stock_status === 'in_stock' ? '#059669' : '#d97706' }}>
            {item.stock_status === 'in_stock' ? 'In Stock' : 'On Request'}
          </span>
          <span style={{ fontSize: '0.75rem', color: C.blue, fontWeight: 700 }}>View →</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', quantity: '', message: '' });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.email) { setQuoteError('Name and email are required.'); return; }
    setQuoteSubmitting(true); setQuoteError('');
    try {
      await api.post('/public/contact', {
        name: quoteForm.name,
        email: quoteForm.email,
        subject: `Quote Request: ${product?.name || ''} (${product?.catalog_number || ''})`,
        message: `Product: ${product?.name}\nCatalog #: ${product?.catalog_number}\nCAS: ${product?.cas_number || 'N/A'}\nQuantity needed: ${quoteForm.quantity || 'Not specified'}\n\n${quoteForm.message}`,
        product_interest: product?.catalog_number || '',
      });
      setQuoteSent(true);
    } catch {
      setQuoteError('Failed to send. Please try again or email us directly.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/public/catalog/${id}`)
      .then(r => {
        setProduct(r.data.data);
        setRelated(r.data.related || []);
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24 }}>
        <div style={{ fontSize: '3rem' }}>🔬</div>
        <h2 style={{ fontWeight: 800, color: C.text }}>Product Not Found</h2>
        <p style={{ color: C.sub }}>This item may have been removed or is unavailable.</p>
        <Link to="/catalog" style={{ padding: '10px 24px', background: C.blue, color: '#fff', borderRadius: 9, textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem' }}>
          ← Back to Catalog
        </Link>
      </div>
    );
  }

  const isImp = product.category === 'impurity';
  const accentColor = isImp ? C.blue : C.green;

  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: [
      product.description,
      product.cas_number ? `CAS Number: ${product.cas_number}` : '',
      product.molecular_formula ? `Molecular Formula: ${product.molecular_formula}` : '',
      product.pharmacopeia ? `Pharmacopeia: ${product.pharmacopeia}` : '',
    ].filter(Boolean).join('. '),
    sku: product.catalog_number || String(product.id),
    image: product.image ? `https://www.prasilabs.com/products/${product.image}` : 'https://www.prasilabs.com/prasilabs-logo.png',
    brand: { '@type': 'Brand', name: 'PRASI LABS' },
    manufacturer: { '@id': 'https://www.prasilabs.com/#organization' },
    offers: {
      '@type': 'Offer',
      url: `https://www.prasilabs.com/catalog/${product.id}`,
      availability: product.stock_status === 'in_stock'
        ? 'https://schema.org/InStock'
        : 'https://schema.org/PreOrder',
      seller: { '@id': 'https://www.prasilabs.com/#organization' },
    },
    additionalProperty: [
      product.cas_number && { '@type': 'PropertyValue', name: 'CAS Number', value: product.cas_number },
      product.molecular_formula && { '@type': 'PropertyValue', name: 'Molecular Formula', value: product.molecular_formula },
      product.molecular_weight && { '@type': 'PropertyValue', name: 'Molecular Weight', value: product.molecular_weight },
      product.pharmacopeia && { '@type': 'PropertyValue', name: 'Pharmacopeia Grade', value: product.pharmacopeia },
      product.catalog_number && { '@type': 'PropertyValue', name: 'Catalog Number', value: product.catalog_number },
    ].filter(Boolean),
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.prasilabs.com/' },
      { '@type': 'ListItem', position: 2, name: 'Catalog', item: 'https://www.prasilabs.com/catalog' },
      { '@type': 'ListItem', position: 3, name: product.name, item: `https://www.prasilabs.com/catalog/${product.id}` },
    ],
  };

  const seaKeywords = [
    product.name,
    product.cas_number ? `CAS ${product.cas_number}` : '',
    product.catalog_number ? `catalog ${product.catalog_number}` : '',
    'reference standard', 'pharmaceutical impurity', 'buy reference standard India',
    product.pharmacopeia ? `${product.pharmacopeia} grade` : '',
  ].filter(Boolean).join(', ');
  const catLabel = isImp ? 'Impurity Standard' : 'Reference Standard';

  return (
    <>
      <SEO
        title={`${product.name}${product.catalog_number ? ` (${product.catalog_number})` : ''} — Pharmaceutical Reference Standard`}
        description={`Buy ${product.name}${product.cas_number ? ` CAS ${product.cas_number}` : ''} — certified pharmaceutical ${isImp ? 'impurity' : 'reference'} standard from PRASI LABS. ${product.pharmacopeia ? product.pharmacopeia + ' grade.' : ''} Available in multiple pack sizes. Request a quote online.`}
        keywords={seaKeywords}
        canonical={`/catalog/${product.id}`}
        ogImage={product.image ? `https://www.prasilabs.com/products/${product.image}` : undefined}
        ogType="product"
        jsonLd={[productLd, breadcrumbLd]}
      />
      <div style={{ background: C.bg }}>
        <style>{`
        .pub-quote-input::placeholder { color: rgba(255,255,255,0.45); }
        @media (max-width: 768px) {
          .pub-product-detail-wrap { padding: 24px 16px !important; }
          .pub-related-grid { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important; }
          .pub-cta-banner { padding: 32px 20px !important; }
        }
        @media (max-width: 480px) {
          .pub-related-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

        {/* ── Breadcrumb bar ────────────────────────────────── */}
        <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '12px 24px' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: C.muted }}>
            <Link to="/" style={{ color: C.muted, textDecoration: 'none' }}>Home</Link>
            <span>/</span>
            <Link to="/catalog" style={{ color: C.muted, textDecoration: 'none' }}>Catalog</Link>
            <span>/</span>
            <span style={{ color: C.text, fontWeight: 600, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</span>
          </div>
        </div>

        {/* ── Main product section ──────────────────────────── */}
        <div className="pub-product-detail-wrap" style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
          <div className="pub-product-grid">

            {/* Left: image + quick actions */}
            <FadeIn direction="left">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Product image card */}
                <div style={{ background: C.white, borderRadius: 18, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                  <ProductImage item={product} height={280} large />
                  {/* Category + stock below image */}
                  <div style={{ padding: '16px 20px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: isImp ? C.blueLt : '#f0fdf4', color: accentColor }}>{catLabel}</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: product.stock_status === 'in_stock' ? '#d1fae5' : '#fef3c7', color: product.stock_status === 'in_stock' ? '#059669' : '#d97706' }}>
                      {product.stock_status === 'in_stock' ? 'In Stock' : 'On Request'}
                    </span>
                    {product.pharmacopeia && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '4px 12px', borderRadius: 20, background: C.bg, color: C.sub, border: `1px solid ${C.border}` }}>{product.pharmacopeia}</span>
                    )}
                  </div>
                </div>

                {/* Scan to share QR */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <QRWidget
                    value={window.location.href}
                    size={80}
                    dark="#0a1628"
                    label="Scan to share"
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 4 }}>Share this product</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>
                      Scan the QR code on mobile to open this product page — or share it with your team.
                    </div>
                  </div>
                </div>

                {/* Inline quote form card */}
                <div style={{ background: 'linear-gradient(135deg, #0a1628, #1e3a8a)', borderRadius: 16, padding: '24px', color: '#fff' }}>
                  <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>Request a Quote</div>
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: 16 }}>
                    We respond within 24 business hours with pricing &amp; CoA details.
                  </p>

                  {quoteSent ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: 6 }}>Quote Request Sent!</div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>We'll reply to your email within one business day.</div>
                      <button onClick={() => setQuoteSent(false)} style={{ marginTop: 14, padding: '8px 18px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>
                        Send another request
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleQuoteSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <input
                        className="pub-quote-input"
                        placeholder="Your Name *"
                        value={quoteForm.name}
                        onChange={e => setQuoteForm(f => ({ ...f, name: e.target.value }))}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.83rem', outline: 'none' }}
                      />
                      <input
                        className="pub-quote-input"
                        type="email"
                        placeholder="Email Address *"
                        value={quoteForm.email}
                        onChange={e => setQuoteForm(f => ({ ...f, email: e.target.value }))}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.83rem', outline: 'none' }}
                      />
                      <input
                        className="pub-quote-input"
                        placeholder="Quantity needed (e.g. 5 mg, 1 g)"
                        value={quoteForm.quantity}
                        onChange={e => setQuoteForm(f => ({ ...f, quantity: e.target.value }))}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.83rem', outline: 'none' }}
                      />
                      <textarea
                        className="pub-quote-input"
                        placeholder="Additional requirements (optional)"
                        value={quoteForm.message}
                        onChange={e => setQuoteForm(f => ({ ...f, message: e.target.value }))}
                        rows={2}
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.83rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                      {quoteError && <div style={{ fontSize: '0.75rem', color: '#fca5a5' }}>{quoteError}</div>}
                      <button type="submit" disabled={quoteSubmitting}
                        style={{ padding: '12px', background: '#fff', color: C.blue, borderRadius: 10, fontWeight: 800, fontSize: '0.88rem', border: 'none', cursor: quoteSubmitting ? 'not-allowed' : 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', opacity: quoteSubmitting ? 0.7 : 1, transition: 'opacity 0.15s' }}>
                        {quoteSubmitting ? 'Sending…' : 'Send Quote Request →'}
                      </button>
                    </form>
                  )}

                  <button onClick={() => navigate('/catalog')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', marginTop: 10, padding: '10px', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)', borderRadius: 10, fontWeight: 600, fontSize: '0.82rem', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer' }}>
                    ← Back to Catalog
                  </button>
                </div>

                {/* Download CoA note */}
                <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f0fdf4', color: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.84rem', color: C.text, marginBottom: 4 }}>Certificate of Analysis</div>
                    <div style={{ fontSize: '0.78rem', color: C.sub, lineHeight: 1.6 }}>Lot-specific CoA with HPLC, NMR, and MS data included with every shipment.</div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Right: product details */}
            <FadeIn direction="left" delay={80}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Name + catalog number */}
                <div>
                  <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: accentColor, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 8 }}>{product.catalog_number}</div>
                  <h1 style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.75rem)', fontWeight: 900, color: C.text, lineHeight: 1.25, marginBottom: 12, letterSpacing: '-0.3px' }}>{product.name}</h1>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {product.therapeutic_class && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#fef3c7', color: '#92400e' }}>{product.therapeutic_class}</span>
                    )}
                    {product.regulatory_class && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#f5f3ff', color: '#7c3aed' }}>{product.regulatory_class}</span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {product.description && (
                  <div style={{ background: C.blueLt, borderRadius: 12, padding: '16px 18px', border: `1px solid ${C.blueMd}` }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Description</div>
                    <p style={{ fontSize: '0.875rem', color: C.sub, lineHeight: 1.8, margin: 0 }}>{product.description}</p>
                  </div>
                )}

                {/* Chemical properties */}
                <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: '22px 24px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Chemical Properties</div>
                  <SpecRow label="CAS Number" value={product.cas_number} mono highlight />
                  <SpecRow label="Molecular Formula" value={product.molecular_formula} mono />
                  <SpecRow label="Molecular Weight" value={product.molecular_weight ? `${product.molecular_weight} g/mol` : null} />
                  <SpecRow label="Purity" value={product.purity} highlight />
                  <SpecRow label="HSN Code" value={product.hsn_code} mono />
                </div>

                {/* Supply & regulatory */}
                <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: '22px 24px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Supply & Regulatory</div>
                  <SpecRow label="Pack Sizes" value={product.pack_sizes} />
                  <SpecRow label="Lead Time" value={product.lead_time} />
                  <SpecRow label="Storage" value={product.storage} />
                  <SpecRow label="Pharmacopeia" value={product.pharmacopeia} />
                  <SpecRow label="Regulatory Class" value={product.regulatory_class} />
                  <SpecRow label="Therapeutic Class" value={product.therapeutic_class} />
                </div>

                {/* Analytical methods strip */}
                <div style={{ background: 'linear-gradient(135deg, #f0f4ff, #e8f0fe)', borderRadius: 14, padding: '18px 20px', border: `1px solid ${C.blueMd}` }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Analytical Characterization Included</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {['HPLC Purity', 'NMR', 'LC-MS', 'IR Spectroscopy', 'Lot-specific CoA'].map(m => (
                      <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600, color: C.blue }}>
                        <svg width="12" height="12" fill="none" stroke={C.green} strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                        {m}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* ── Related Products ─────────────────────────────── */}
          {related.length > 0 && (
            <div style={{ marginTop: 72 }}>
              <FadeIn>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'inline-block', background: C.blueLt, color: C.blue, fontSize: '0.7rem', fontWeight: 700, padding: '3px 12px', borderRadius: 20, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Related Products
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 900, color: C.text, letterSpacing: '-0.3px' }}>Similar Compounds</h2>
                    <p style={{ fontSize: '0.875rem', color: C.sub, marginTop: 4 }}>
                      Based on therapeutic class, regulatory classification, and chemical composition
                    </p>
                  </div>
                  <Link to="/catalog" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: C.blue, fontWeight: 700, textDecoration: 'none', padding: '8px 18px', border: `1.5px solid ${C.blue}`, borderRadius: 9 }}>
                    View All
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </Link>
                </div>
              </FadeIn>

              {/* Similarity legend */}
              <FadeIn delay={80}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                  {[
                    { color: '#fef3c7', text: '#92400e', label: 'Same therapeutic class' },
                    { color: '#f5f3ff', text: '#7c3aed', label: 'Same regulatory class' },
                    { color: C.blueLt, text: C.blue, label: 'Same category' },
                  ].map(t => (
                    <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: C.sub }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color, border: `1px solid ${t.text}33`, display: 'inline-block' }} />
                      {t.label}
                    </div>
                  ))}
                </div>
              </FadeIn>

              <div className="pub-related-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
                {related.map((item, i) => (
                  <FadeIn key={item.id} delay={i * 60} direction="up">
                    <RelatedCard item={item} />
                  </FadeIn>
                ))}
              </div>
            </div>
          )}

          {/* ── CTA banner ────────────────────────────────────── */}
          <FadeIn delay={100}>
            <div className="pub-cta-banner" style={{ marginTop: 64, background: 'linear-gradient(135deg, #0a1628, #1e3a8a, #1d4ed8)', borderRadius: 20, padding: '44px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <svg style={{ position: 'absolute', right: 40, top: '50%', transform: 'translateY(-50%)', opacity: 0.06 }} width="200" height="180" viewBox="0 0 200 180">
                <circle cx="50" cy="90" r="28" stroke="white" strokeWidth="2.5" fill="none" />
                <circle cx="100" cy="35" r="28" stroke="white" strokeWidth="2.5" fill="none" />
                <circle cx="150" cy="90" r="28" stroke="white" strokeWidth="2.5" fill="none" />
                <circle cx="100" cy="145" r="28" stroke="white" strokeWidth="2.5" fill="none" />
                <line x1="72" y1="71" x2="83" y2="55" stroke="white" strokeWidth="2.5" />
                <line x1="117" y1="55" x2="128" y2="71" stroke="white" strokeWidth="2.5" />
                <line x1="128" y1="109" x2="117" y2="125" stroke="white" strokeWidth="2.5" />
                <line x1="83" y1="125" x2="72" y2="109" stroke="white" strokeWidth="2.5" />
              </svg>
              <div style={{ position: 'relative', zIndex: 1, maxWidth: 580, margin: '0 auto' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Custom Synthesis Available</div>
                <h3 style={{ color: '#fff', fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontWeight: 900, marginBottom: 12, lineHeight: 1.2 }}>
                  Need a Modified or Unlisted Compound?
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', marginBottom: 28, lineHeight: 1.75 }}>
                  Our synthesis team can prepare analogs, isotope-labelled versions, and custom derivatives of {product.name} to your exact specifications.
                </p>
                <Link to="/contact"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 32px', background: '#fff', color: C.blue, borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '0.95rem', boxShadow: '0 4px 20px rgba(0,0,0,0.22)' }}>
                  Discuss Custom Synthesis
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  );
}
